import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserPlus, X } from "lucide-react";
import api from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { Button } from "@/components/ui/button";

/**
 * Centre-screen alert when a lead lands in YOUR queue.
 *
 * Shown only to the person the lead was assigned to. The clinic takes ~35 leads
 * a day, so announcing every one to every user would put a modal over whatever
 * they were typing every fourteen minutes. Split across the telecallers it is
 * closer to one an hour, which is a prompt rather than an interruption. Managers
 * see them on the Leads board, which already refreshes live.
 *
 * It also never fires for a lead you created yourself — you are already looking
 * at it.
 *
 * The SSE stream is a broadcast to every logged-in user, so the event carries
 * ids and nothing else. The lead's name and number come from a fetch, which goes
 * through the access-controlled endpoint: if the lead is not yours to see, the
 * request fails and no popup appears.
 */
const DISMISS_AFTER_MS = 30000;

export default function NewLeadPopup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const timerRef = useRef(null);

  const current = queue[0] || null;

  const shift = useCallback(() => setQueue((q) => q.slice(1)), []);

  useLiveEvents(
    useCallback(
      (event) => {
        if (event?.type !== "lead.created" || !user?.id) return;
        if (event.assigned_to !== user.id) return;   // not mine
        if (event.actor_id === user.id) return;      // I just created it
        api
          .get(`/leads/${event.lead_id}`)
          .then(({ data }) => {
            setQueue((q) =>
              q.some((l) => l.id === data.id) ? q : [...q, data].slice(0, 20));
          })
          .catch(() => {});                          // no access, or deleted
      },
      [user?.id],
    ),
  );

  // Each lead gets its own countdown, restarted whenever the front of the queue
  // changes — so a second lead arriving does not inherit the first one's
  // remaining time and vanish before it has been read.
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!current) return undefined;
    timerRef.current = setTimeout(shift, DISMISS_AFTER_MS);
    return () => clearTimeout(timerRef.current);
  }, [current, shift]);

  // Escape dismisses, like every other dialog in the app.
  useEffect(() => {
    if (!current) return undefined;
    const onKey = (e) => { if (e.key === "Escape") shift(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [current, shift]);

  if (!current) return null;

  const open = () => {
    const id = current.id;
    shift();
    navigate(`/leads/${id}`);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
         role="dialog" aria-modal="true" aria-labelledby="new-lead-title">
      {/* Click-away dismisses. Deliberately not a full-opacity scrim: this
          interrupts work, so the page behind it should stay visible. */}
      <div className="absolute inset-0 bg-navy/30 backdrop-blur-[2px]" onClick={shift} />

      <div className="relative w-full max-w-sm rounded-2xl border border-navy/10 bg-white p-5 shadow-2xl animate-fade-up">
        <button onClick={shift} aria-label="Dismiss"
                className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground hover:bg-navy/5 hover:text-navy">
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-700">
            <UserPlus className="h-4 w-4" />
          </span>
          <p id="new-lead-title" className="font-display text-sm font-semibold text-navy">
            New lead assigned to you
          </p>
        </div>

        <p className="mt-3 truncate text-lg font-semibold text-ink">
          {current.name || "Unnamed lead"}
        </p>
        <p className="text-sm tabular-nums text-muted-foreground">
          {maskPhone(current.mobile || current.whatsapp) || "No number"}
        </p>

        <div className="mt-2 flex flex-wrap gap-1.5">
          {current.source && (
            <span className="rounded-full bg-navy/5 px-2 py-0.5 text-[11px] font-medium text-navy">
              {current.source}
            </span>
          )}
          {current.interested_service && (
            <span className="rounded-full bg-navy/5 px-2 py-0.5 text-[11px] font-medium text-navy">
              {current.interested_service}
            </span>
          )}
          {current.city && (
            <span className="rounded-full bg-navy/5 px-2 py-0.5 text-[11px] font-medium text-navy">
              {current.city}
            </span>
          )}
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button onClick={open} className="flex-1 bg-navy text-white hover:bg-navy-hover">
            Open lead
          </Button>
          <Button variant="outline" onClick={shift}>Later</Button>
        </div>

        {queue.length > 1 && (
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            {queue.length - 1} more waiting
          </p>
        )}
      </div>
    </div>
  );
}
